/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {GenericObject} from '@ion/core/src/types/entities';
import {BulkOperationResults, ExtendedResults} from '@ion/core/src/types/services';
import {
  IMPORT_CONFIRM,
  IMPORT_ERROR,
  IMPORT_REQUEST,
  IMPORT_RESPONSE
} from '../actions/importer';
import {ExportContext} from './../../types/redux';

export interface ImportAction {
  entity: ExportContext;
  result: BulkOperationResults | ExtendedResults;
  valid: boolean;
}

export interface EntityImportState {
  pending: {},
  entity: ExportContext | null;
  result: BulkOperationResults | ExtendedResults | null;
  valid: boolean;
  isImporting: boolean;
  isComplete: boolean;
  isError: boolean;
  show: boolean;
};

export interface ImportState {
  current: EntityImportState;
}

const defaultState = {
  pending : {
    [ExportContext.asset]                : false,
    [ExportContext.device]               : false,
    [ExportContext.operator]             : false,
    [ExportContext.group]                : false,
    [ExportContext.ioProfile]            : false,
    [ExportContext.role]                 : false,
    [ExportContext.user]                 : false,
    [ExportContext.notification]         : false,
    [ExportContext.schedule]             : false,
    [ExportContext.serviceSchedule]      : false,
    [ExportContext.serviceItem]          : false,
    [ExportContext.maintenanceDashboard] : false,
    [ExportContext.maintenanceSchedule]  : false,
    [ExportContext.maintenanceHistory]   : false
  },
  entity      : null,
  result      : null,
  valid       : false,
  isImporting : false,
  isComplete  : false,
  isError     : false,
  show        : false
};

// defines the initial state for the reducer ...
export const initialState: ImportState = {
  current : {...defaultState}
};

// defines this reducers reducer functions ...
const reducers = {
  [IMPORT_REQUEST] : ({current}: ImportState, {entity}: ImportAction) => {
    current.pending[entity] = true;
    current.entity          = entity;
    current.isImporting     = true;
    current.show            = true;
    current.isComplete      = false;
    current.isError         = false;
  },
  [IMPORT_RESPONSE] : ({current}: ImportState, {entity, result, valid}: ImportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.result          = result;
    current.valid           = valid;
    current.isComplete      = true;
    current.show            = true;
    current.isImporting     = false;
    current.isError         = false;
  },
  [IMPORT_ERROR] : ({current}: ImportState, {entity}: ImportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.result          = null;
    current.isError         = true;
    current.show            = true;
    current.isComplete      = false;
    current.isImporting     = false;
  },
  [IMPORT_CONFIRM] : ({current}: ImportState, {entity}: ImportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.isImporting     = false;
    current.show            = false;
    current.isComplete      = true;
    current.isError         = false;
  }
};

export const isImporting = (pending: GenericObject, entity: ExportContext): boolean => {
  return pending[entity] === true;
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ImportState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
